/**
 * PresetBrowser — side panel for saving, loading, and deleting presets.
 *
 * Same slide-in-from-right layout as ModMatrix. Top: a save row (name input
 * + Save button). Bottom: scrollable list of presets, each a row with the
 * name (click to load), an overwrite button, and a delete button. Empty
 * state when no presets exist.
 */

import type { PresetManager } from '@ac-slayer/synth-presets';
import type { Preset } from '@ac-slayer/synth-presets';
import { DEFAULT_PRESET_CATEGORY, mintPresetId, CURRENT_SCHEMA_VERSION } from '@ac-slayer/synth-presets';
import { migratePreset } from '@ac-slayer/synth-presets';
import type { AuthManager, AuthState } from '@/auth/AuthManager';

export interface PresetBrowserOptions {
  readonly manager: PresetManager;
  readonly auth: AuthManager;
  /**
   * Read-only preset bank shipped with the app. Rendered in its own section
   * above the user's presets, and always visible even when signed out —
   * factory patches don't require auth to hear.
   */
  readonly factoryPresets?: readonly Preset[];
  /**
   * Optional — when supplied, a "Test" button appears in the panel
   * header that triggers a short triad so the user can audition the
   * currently-loaded patch without leaving the preset browser. Same
   * handler the toolbar Test button fires so UX stays consistent.
   */
  readonly onTest?: () => void;
}

/** Lightweight "currently-active preset" summary, for external UIs. */
export interface ActivePresetInfo {
  readonly id: string;
  readonly name: string;
  readonly category: string;
  /** True when this is a factory-shipped patch; false for user saves. */
  readonly factory: boolean;
}

export type ActivePresetListener = (info: ActivePresetInfo | null) => void;

export class PresetBrowser {
  readonly element: HTMLElement;
  private readonly manager: PresetManager;
  private readonly auth: AuthManager;
  private readonly unsubscribeManager: () => void;
  private readonly unsubscribeAuth: () => void;

  private readonly nameInput: HTMLInputElement;
  private readonly categorySelect: HTMLSelectElement;
  private readonly newCategoryInput: HTMLInputElement;
  private readonly saveBtn: HTMLButtonElement;
  private readonly listEl: HTMLElement;
  private readonly emptyEl: HTMLElement;
  private readonly signInForm: HTMLElement;
  private readonly formRow: HTMLElement;
  private readonly listContainer: HTMLElement;
  private readonly authMessageEl: HTMLElement;
  private readonly factoryContainer: HTMLElement;
  private readonly factoryListEl: HTMLElement;
  private factoryPresets: readonly Preset[];

  private readonly searchInput: HTMLInputElement;
  private searchQuery = '';

  private open = false;
  private presets: Preset[] = [];
  private activePresetId: string | null = null;
  /**
   * Summary of the currently-active patch for external UIs (toolbar
   * label, window title). A tiny shape rather than the full Preset so
   * the save path — which doesn't round-trip a fresh preset from the
   * store — can still emit without fabricating a junk Preset object.
   */
  private activeSummary: ActivePresetInfo | null = null;
  private readonly activeListeners = new Set<ActivePresetListener>();

  constructor(opts: PresetBrowserOptions) {
    this.manager = opts.manager;
    this.auth = opts.auth;
    this.factoryPresets = opts.factoryPresets ?? [];

    this.element = document.createElement('aside');
    this.element.className = 'preset-browser';
    this.element.setAttribute('aria-label', 'Preset browser');
    this.element.hidden = true;

    // ── Header
    const header = document.createElement('header');
    header.className = 'preset-browser-header';
    header.innerHTML = `<h2 class="preset-browser-title">Presets</h2>`;

    // Header actions — Import + Test (optional) + Close. All sit at
    // the right edge. Test fires the caller-supplied audition handler
    // so the preset panel can double as a "here's what I sound like"
    // probe. Import opens a hidden file picker for loading JSON
    // presets exported from this synth (or another user's bank).
    const importBtn = document.createElement('button');
    importBtn.type = 'button';
    importBtn.className = 'btn btn-sm';
    importBtn.textContent = 'Import';
    importBtn.title = 'Load a preset from a JSON file';
    const importFileInput = document.createElement('input');
    importFileInput.type = 'file';
    importFileInput.accept = '.json,application/json';
    importFileInput.style.display = 'none';
    importFileInput.addEventListener('change', () => {
      const file = importFileInput.files?.[0];
      if (file) void this.handleImport(file);
      // Reset so picking the same file twice still fires `change`.
      importFileInput.value = '';
    });
    importBtn.addEventListener('click', () => importFileInput.click());
    header.append(importBtn, importFileInput);

    if (opts.onTest) {
      const headerTestBtn = document.createElement('button');
      headerTestBtn.type = 'button';
      headerTestBtn.className = 'btn btn-sm';
      headerTestBtn.textContent = 'Test';
      headerTestBtn.title = 'Play a short triad with the current patch';
      headerTestBtn.addEventListener('click', () => opts.onTest?.());
      header.appendChild(headerTestBtn);
    }

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'btn btn-sm';
    closeBtn.textContent = 'Close';
    closeBtn.addEventListener('click', () => this.setOpen(false));
    header.appendChild(closeBtn);

    this.element.appendChild(header);

    // ── Search row — filters both factory and user sections in place by
    // name (case-insensitive substring) and tags. Empty query shows
    // everything; clearing the input restores the full list. Esc also
    // clears so a stuck filter is one keystroke away.
    const searchRow = document.createElement('div');
    searchRow.className = 'preset-browser-search';
    this.searchInput = document.createElement('input');
    this.searchInput.type = 'search';
    this.searchInput.className = 'preset-browser-input mono preset-browser-search-input';
    this.searchInput.placeholder = 'search presets…';
    this.searchInput.setAttribute('aria-label', 'Search presets');
    this.searchInput.addEventListener('input', () => {
      this.searchQuery = this.searchInput.value.trim().toLowerCase();
      this.render();
    });
    this.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.searchInput.value !== '') {
        this.searchInput.value = '';
        this.searchQuery = '';
        this.render();
        e.preventDefault();
      }
    });
    searchRow.appendChild(this.searchInput);
    this.element.appendChild(searchRow);

    // ── Factory bank (always visible, no auth required)
    this.factoryContainer = document.createElement('div');
    this.factoryContainer.className = 'preset-browser-factory';
    const factoryHeading = document.createElement('div');
    factoryHeading.className = 'preset-browser-section-heading label';
    factoryHeading.textContent = 'Factory';
    this.factoryContainer.appendChild(factoryHeading);
    this.factoryListEl = document.createElement('div');
    this.factoryListEl.className = 'preset-browser-list';
    this.factoryContainer.appendChild(this.factoryListEl);
    this.factoryContainer.hidden = this.factoryPresets.length === 0;
    this.element.appendChild(this.factoryContainer);
    this.renderFactory();

    // ── Sign-in form (shown when logged out)
    this.signInForm = this.buildSignInForm();
    this.element.appendChild(this.signInForm);

    // ── Save row (shown when logged in)
    this.formRow = document.createElement('div');
    this.formRow.className = 'preset-browser-form';

    this.nameInput = document.createElement('input');
    this.nameInput.type = 'text';
    this.nameInput.className = 'preset-browser-input mono';
    this.nameInput.placeholder = 'name this patch…';
    this.nameInput.setAttribute('aria-label', 'Preset name');
    this.nameInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.handleSave();
    });

    // Category: real <select> so every option is visible in the
    // dropdown (unlike a datalist, which native browsers filter by the
    // current prefix). The last option is "— new category —"; picking
    // it reveals the freeform text input for typing a fresh one. Same
    // pattern as the update dialog — keeps save and overwrite UX
    // consistent.
    this.categorySelect = document.createElement('select');
    this.categorySelect.className = 'preset-browser-input mono preset-browser-category-input';
    this.categorySelect.setAttribute('aria-label', 'Preset category');
    this.categorySelect.title = 'Pick an existing category or choose "— new category —"';

    this.newCategoryInput = document.createElement('input');
    this.newCategoryInput.type = 'text';
    this.newCategoryInput.className = 'preset-browser-input mono preset-browser-category-new';
    this.newCategoryInput.placeholder = 'new category name';
    this.newCategoryInput.hidden = true;
    this.newCategoryInput.setAttribute('aria-label', 'New category name');
    this.newCategoryInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.handleSave();
    });

    this.categorySelect.addEventListener('change', () => {
      const isNew = this.categorySelect.value === '__new__';
      this.newCategoryInput.hidden = !isNew;
      if (isNew) this.newCategoryInput.focus();
    });

    this.saveBtn = document.createElement('button');
    this.saveBtn.type = 'button';
    this.saveBtn.className = 'btn btn-sm';
    this.saveBtn.textContent = '+ Save';
    this.saveBtn.addEventListener('click', () => this.handleSave());

    this.formRow.append(
      this.nameInput,
      this.categorySelect,
      this.newCategoryInput,
      this.saveBtn,
    );
    this.element.appendChild(this.formRow);
    // Seed the select up front so the dropdown has values from first
    // paint, even before any user presets are fetched.
    this.refreshCategoryDatalist();

    // ── List + empty state (shown when logged in)
    this.listContainer = document.createElement('div');
    this.listContainer.className = 'preset-browser-list-container';

    const userHeading = document.createElement('div');
    userHeading.className = 'preset-browser-section-heading label';
    userHeading.textContent = 'My Presets';
    this.listContainer.appendChild(userHeading);

    this.listEl = document.createElement('div');
    this.listEl.className = 'preset-browser-list';
    this.listContainer.appendChild(this.listEl);

    this.emptyEl = document.createElement('div');
    this.emptyEl.className = 'preset-browser-empty label';
    this.emptyEl.textContent = 'No saved presets — dial in a sound and hit Save.';
    this.listContainer.appendChild(this.emptyEl);

    this.element.appendChild(this.listContainer);

    // Shared area for transient auth messages ("Check your email to confirm…").
    this.authMessageEl = document.createElement('div');
    this.authMessageEl.className = 'preset-browser-message label';
    this.authMessageEl.hidden = true;
    this.element.appendChild(this.authMessageEl);

    this.unsubscribeManager = this.manager.subscribe(() => void this.refresh());
    this.unsubscribeAuth = this.auth.subscribe((state) => this.handleAuthChange(state));
  }

  setOpen(open: boolean, opts: { focusSearch?: boolean } = {}): void {
    this.open = open;
    this.element.hidden = !open;
    this.element.classList.toggle('open', open);
    if (open && this.auth.isSignedIn) void this.refresh();
    // Focus the search field when the panel opens — fastest path to
    // "find a patch" is type, not click. Defer one frame so the
    // panel's open transition doesn't fight focus(). Caller can
    // suppress this when the panel is being opened programmatically
    // (e.g. the fart-storm "show off Cheek Flapper" flow) so the
    // user can keep playing the synth via QWERTY without typing
    // into the search field by accident.
    const focusSearch = opts.focusSearch ?? true;
    if (open && focusSearch) {
      requestAnimationFrame(() => {
        if (this.open) this.searchInput.focus();
      });
    }
  }

  toggle(): void {
    this.setOpen(!this.open);
  }

  dispose(): void {
    this.unsubscribeManager();
    this.unsubscribeAuth();
    this.activeListeners.clear();
  }

  /**
   * Subscribe to active-preset changes. Fires immediately with the
   * current value and then on every subsequent save / load / delete.
   * Returns an unsubscribe function. UIs outside the panel (toolbar
   * label, window title) use this to reflect "which patch am I hearing
   * right now".
   */
  subscribeActive(listener: ActivePresetListener): () => void {
    this.activeListeners.add(listener);
    listener(this.activeSummary);
    return () => {
      this.activeListeners.delete(listener);
    };
  }

  /**
   * Clear the "currently-loaded preset" label. Called when the patch
   * has diverged so far from any saved preset that the name is
   * misleading — e.g. the user hits Randomize. Same fan-out as
   * `setActive(null)` internally; just a public entry point.
   */
  clearActive(): void {
    if (this.activeSummary === null) return;
    this.setActive(null);
  }

  /**
   * Replace the factory preset bank and re-render. Used for runtime
   * unlocks (the secret fart bank reveals itself once the user types
   * the easter-egg phrase). Refreshes the category dropdown too so
   * any new categories the new bank introduces show up in Save.
   */
  setFactoryPresets(presets: readonly Preset[]): void {
    this.factoryPresets = presets;
    this.factoryContainer.hidden = presets.length === 0;
    this.renderFactory();
    this.refreshCategoryDatalist();
  }

  /**
   * Load a factory preset programmatically — same code path the
   * Load button uses. Public so external callers (the fart-storm
   * modal "load Cheek Flapper on dismiss" flow) can drive a load
   * without faking a click event.
   */
  loadFactoryPreset(preset: Preset): void {
    this.handleFactoryLoad(preset);
  }

  /**
   * Scroll a specific preset's row into view. Used by the fart-storm
   * modal to highlight Cheek Flapper after an unlock. Silently no-ops
   * if the row isn't in the DOM (e.g., the search filter is hiding it).
   */
  scrollToPreset(id: string): void {
    const row = this.element.querySelector<HTMLElement>(
      `[data-preset-id="${CSS.escape(id)}"]`,
    );
    if (!row) return;
    row.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * Central setter — all four code paths (load, save, overwrite,
   * factory-load) funnel through here so listener fan-out and the
   * id/summary pair stay synchronized. Passing `null` clears both.
   */
  private setActive(info: ActivePresetInfo | null): void {
    this.activeSummary = info;
    this.activePresetId = info?.id ?? null;
    for (const fn of this.activeListeners) fn(info);
  }

  /** Build a summary from a loaded Preset. `factory` must be supplied
   *  because factory ids don't differ structurally from user ids. */
  private toInfo(preset: Preset, factory: boolean): ActivePresetInfo {
    return {
      id: preset.id,
      name: preset.name,
      category: preset.category,
      factory,
    };
  }

  // ── Auth UI ──────────────────────────────────────────────────────────

  private buildSignInForm(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'preset-browser-auth';

    const intro = document.createElement('p');
    intro.className = 'preset-browser-auth-intro label';
    intro.textContent = 'Sign in to save and sync your presets.';
    wrap.appendChild(intro);

    const email = document.createElement('input');
    email.type = 'email';
    email.autocomplete = 'email';
    email.required = true;
    email.className = 'preset-browser-input mono';
    email.placeholder = 'email';
    email.setAttribute('aria-label', 'Email');
    wrap.appendChild(email);

    const password = document.createElement('input');
    password.type = 'password';
    password.autocomplete = 'current-password';
    password.required = true;
    password.className = 'preset-browser-input mono';
    password.placeholder = 'password';
    password.setAttribute('aria-label', 'Password');
    wrap.appendChild(password);

    const actions = document.createElement('div');
    actions.className = 'preset-browser-auth-actions';

    const signInBtn = document.createElement('button');
    signInBtn.type = 'button';
    signInBtn.className = 'btn btn-sm';
    signInBtn.textContent = 'Sign in';

    const signUpBtn = document.createElement('button');
    signUpBtn.type = 'button';
    signUpBtn.className = 'btn btn-sm';
    signUpBtn.textContent = 'Sign up';

    const errorEl = document.createElement('div');
    errorEl.className = 'preset-browser-auth-error label';
    errorEl.hidden = true;

    const setError = (msg: string | null): void => {
      if (!msg) {
        errorEl.hidden = true;
        errorEl.textContent = '';
        return;
      }
      errorEl.hidden = false;
      errorEl.textContent = msg;
    };

    const submit = async (kind: 'signIn' | 'signUp'): Promise<void> => {
      setError(null);
      this.setAuthMessage(null);
      const e = email.value.trim();
      const p = password.value;
      if (!e || !p) {
        setError('Email and password required.');
        return;
      }
      try {
        if (kind === 'signIn') {
          const res = await this.auth.signIn(e, p);
          if (res.error) setError(res.error);
        } else {
          const res = await this.auth.signUp(e, p);
          if (res.error) {
            setError(res.error);
            return;
          }
          if (res.confirmationRequired) {
            this.setAuthMessage(
              `Check ${e} for a confirmation link, then sign in.`,
            );
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    };

    signInBtn.addEventListener('click', () => void submit('signIn'));
    signUpBtn.addEventListener('click', () => void submit('signUp'));

    // Enter submits sign-in by default.
    const onEnter = (e: KeyboardEvent): void => {
      if (e.key === 'Enter') void submit('signIn');
    };
    email.addEventListener('keydown', onEnter);
    password.addEventListener('keydown', onEnter);

    actions.append(signInBtn, signUpBtn);
    wrap.appendChild(actions);
    wrap.appendChild(errorEl);
    return wrap;
  }

  private handleAuthChange(state: AuthState): void {
    const signedIn = state.kind === 'signed-in';
    this.signInForm.hidden = signedIn;
    this.formRow.hidden = !signedIn;
    this.listContainer.hidden = !signedIn;
    if (signedIn) void this.refresh();
    else {
      this.presets = [];
      this.setActive(null);
      this.render();
    }
  }

  private setAuthMessage(msg: string | null): void {
    if (!msg) {
      this.authMessageEl.hidden = true;
      this.authMessageEl.textContent = '';
      return;
    }
    this.authMessageEl.hidden = false;
    this.authMessageEl.textContent = msg;
  }

  // ── Internals ────────────────────────────────────────────────────────

  private async refresh(): Promise<void> {
    try {
      this.presets = await this.manager.list();
    } catch (err) {
      console.warn('[preset-browser] list failed', err);
      this.presets = [];
    }
    this.render();
  }

  private async handleSave(): Promise<void> {
    const name = this.nameInput.value.trim();
    if (!name) {
      this.nameInput.focus();
      return;
    }
    // Prefer the new-category input when the user has "— new category —"
    // selected; otherwise use the selected existing category. Empty new
    // input + "new" sentinel falls back to Uncategorized so we never
    // save a blank string.
    const category =
      this.categorySelect.value === '__new__'
        ? this.newCategoryInput.value.trim() || DEFAULT_PRESET_CATEGORY
        : this.categorySelect.value || DEFAULT_PRESET_CATEGORY;
    let savedId: string;
    try {
      savedId = await this.manager.save(name, { category });
    } catch (err) {
      console.warn('[preset-browser] save failed', err);
      return;
    }
    // The summary is all external UIs need — no full Preset required.
    this.setActive({ id: savedId, name, category, factory: false });
    this.nameInput.value = '';
    // Leave the category selection alone — most users save several
    // presets in the same category in a row; resetting it would force
    // re-selection each time. The "new category" text input does get
    // cleared though, since a freshly-typed one just became an existing
    // category in the list.
    if (this.categorySelect.value === '__new__') {
      // Refresh will rebuild options so the new category appears;
      // select it so the next save lands in the same place.
      this.refreshCategoryDatalist();
      this.categorySelect.value = category;
      this.newCategoryInput.value = '';
      this.newCategoryInput.hidden = true;
    }
  }

  private async handleLoad(preset: Preset): Promise<void> {
    try {
      await this.manager.load(preset.id);
      this.setActive(this.toInfo(preset, false));
      this.render();
    } catch (err) {
      console.warn('[preset-browser] load failed', err);
    }
  }

  private async handleOverwrite(preset: Preset): Promise<void> {
    // Build a fresh category list for the dialog — this guarantees it
    // reflects any categories the user has created or loaded since the
    // browser opened, without relying on a shared-ID datalist that can
    // go stale or be invisible inside the hidden Save row.
    const result = await promptUpdatePreset(preset, this.collectCategories());
    if (!result) return;
    try {
      await this.manager.update(preset.id, {
        name: result.name,
        category: result.category,
      });
      this.setActive({
        id: preset.id,
        name: result.name,
        category: result.category,
        factory: false,
      });
    } catch (err) {
      console.warn('[preset-browser] overwrite failed', err);
    }
  }

  /** All categories the browser currently knows about, sorted for display. */
  private collectCategories(): string[] {
    const seen = new Set<string>();
    const add = (cat: string | undefined): void => {
      if (!cat || cat === DEFAULT_PRESET_CATEGORY) return;
      seen.add(cat);
    };
    for (const p of this.presets) add(p.category);
    for (const p of this.factoryPresets) add(p.category);
    for (const starter of ['Bass', 'Lead', 'Pad', 'Pluck', 'Drone', 'FX', 'Seq']) {
      seen.add(starter);
    }
    return [...seen].sort();
  }

  private async handleDelete(preset: Preset): Promise<void> {
    if (!confirm(`Delete preset "${preset.name}"? This cannot be undone.`)) return;
    try {
      await this.manager.delete(preset.id);
      if (this.activePresetId === preset.id) this.setActive(null);
    } catch (err) {
      console.warn('[preset-browser] delete failed', err);
    }
  }

  /**
   * Substring + tag match against the active search query. Empty
   * query trivially passes — the filter is only narrowing, never
   * reordering. Case-insensitive on both sides.
   */
  private matchesSearch(p: Preset): boolean {
    if (this.searchQuery === '') return true;
    const q = this.searchQuery;
    if (p.name.toLowerCase().includes(q)) return true;
    if (p.category.toLowerCase().includes(q)) return true;
    for (const t of p.tags) {
      if (t.toLowerCase().includes(q)) return true;
    }
    return false;
  }

  private render(): void {
    this.listEl.innerHTML = '';
    const filtered = this.presets.filter((p) => this.matchesSearch(p));
    // Empty-state line distinguishes "you have no presets" from "no
    // presets match your search" — same element, different copy.
    if (this.presets.length === 0) {
      this.emptyEl.hidden = false;
      this.emptyEl.textContent = "No presets yet — tweak the rack and hit Save.";
    } else if (filtered.length === 0) {
      this.emptyEl.hidden = false;
      this.emptyEl.textContent = `No presets match "${this.searchQuery}".`;
    } else {
      this.emptyEl.hidden = true;
    }

    // Group by category while preserving the server's ordering within each
    // group (presets come back sorted by updated_at DESC, which is nice for
    // "most recently tweaked at the top").
    const groups = new Map<string, Preset[]>();
    for (const p of filtered) {
      const cat = p.category || DEFAULT_PRESET_CATEGORY;
      let list = groups.get(cat);
      if (!list) {
        list = [];
        groups.set(cat, list);
      }
      list.push(p);
    }
    // Sort category names for stable display order; Uncategorized sinks
    // to the bottom so named categories appear first.
    const names = [...groups.keys()].sort((a, b) => {
      if (a === DEFAULT_PRESET_CATEGORY) return 1;
      if (b === DEFAULT_PRESET_CATEGORY) return -1;
      return a.localeCompare(b);
    });
    for (const name of names) {
      const heading = document.createElement('div');
      heading.className = 'preset-browser-section-heading label';
      heading.textContent = name;
      this.listEl.appendChild(heading);
      for (const p of groups.get(name)!) {
        this.listEl.appendChild(this.renderRow(p));
      }
    }

    // Refresh the category datalist so Save autocomplete reflects what
    // the user currently has.
    this.refreshCategoryDatalist();

    // Keep the Factory section's active highlight in sync with whatever the
    // user just loaded — cheap to re-render, always eight or so rows.
    this.renderFactory();
  }

  /**
   * Rebuild the category <select> options from every category the browser
   * currently knows about: user presets, factory presets, plus a handful
   * of sensible starters. Runs both at construction (before presets load)
   * and again after each render. Preserves the current selection if the
   * same value still exists post-rebuild.
   *
   * Name kept for compatibility with the original datalist-era call
   * sites; the underlying element is now a proper select so every
   * option renders without prefix filtering.
   */
  private refreshCategoryDatalist(): void {
    const seen = new Set<string>();
    const add = (cat: string | undefined): void => {
      if (!cat || cat === DEFAULT_PRESET_CATEGORY) return;
      seen.add(cat);
    };
    // User's own presets.
    for (const p of this.presets) add(p.category);
    // Factory presets — users often want to save their own under the same
    // bucket as the factory bank (e.g. their own "Pad" alongside SPECTRE PAD).
    for (const p of this.factoryPresets) add(p.category);
    // Seed a few common starter categories so a blank-slate user has a
    // prompt; they're only suggestions, not required.
    for (const starter of ['Bass', 'Lead', 'Pad', 'Pluck', 'Drone', 'FX', 'Seq']) {
      seen.add(starter);
    }
    const options = [...seen].sort();

    const previous = this.categorySelect.value;
    this.categorySelect.innerHTML = '';

    // Uncategorized lives at the top as the default landing spot.
    const uncat = document.createElement('option');
    uncat.value = DEFAULT_PRESET_CATEGORY;
    uncat.textContent = DEFAULT_PRESET_CATEGORY;
    this.categorySelect.appendChild(uncat);

    for (const cat of options) {
      const o = document.createElement('option');
      o.value = cat;
      o.textContent = cat;
      this.categorySelect.appendChild(o);
    }

    // Sentinel — picking it reveals the new-category text input.
    const newOpt = document.createElement('option');
    newOpt.value = '__new__';
    newOpt.textContent = '— new category —';
    this.categorySelect.appendChild(newOpt);

    // Restore prior selection when still valid; otherwise fall back to
    // Uncategorized (the first option).
    if (previous && [...this.categorySelect.options].some((o) => o.value === previous)) {
      this.categorySelect.value = previous;
    } else {
      this.categorySelect.value = DEFAULT_PRESET_CATEGORY;
    }
    // Keep the new-category input in sync with whether the sentinel is
    // currently the chosen option.
    this.newCategoryInput.hidden = this.categorySelect.value !== '__new__';
  }

  private renderFactory(): void {
    this.factoryListEl.innerHTML = '';
    const filtered = this.factoryPresets.filter((p) => this.matchesSearch(p));
    for (const p of filtered) {
      this.factoryListEl.appendChild(this.renderFactoryRow(p));
    }
    // Hide the whole Factory section when the search filter rules out
    // every entry — keeps the panel from showing an orphaned heading
    // with no rows under it.
    this.factoryContainer.hidden =
      this.factoryPresets.length === 0 || filtered.length === 0;
  }

  private renderFactoryRow(preset: Preset): HTMLElement {
    const row = document.createElement('div');
    row.className = 'preset-browser-row preset-browser-row-factory';
    // Tag the row with the preset id so external callers (the
    // fart-storm modal's "scroll to Cheek Flapper" handler, etc.)
    // can locate it via querySelector without traversing internal
    // state.
    row.dataset.presetId = preset.id;
    if (preset.id === this.activePresetId) row.classList.add('active');

    const loadBtn = document.createElement('button');
    loadBtn.type = 'button';
    loadBtn.className = 'preset-browser-load';
    loadBtn.title = `Load ${preset.name}`;

    const nameEl = document.createElement('span');
    nameEl.className = 'preset-browser-name';
    nameEl.textContent = preset.name;

    // Tags double as a description here — the factory bank benefits from a
    // quick "bass/mono" hint since factory names are short and stylized.
    const tagEl = document.createElement('span');
    tagEl.className = 'preset-browser-time mono';
    tagEl.textContent = preset.tags.join(' · ');

    loadBtn.append(nameEl, tagEl);
    loadBtn.addEventListener('click', () => this.handleFactoryLoad(preset));

    // Factory rows get an export action too — useful for sharing
    // factory patches as a starter, or just grabbing the JSON to
    // tweak offline. No Update / Delete since factory presets are
    // immutable on the wire.
    const exportBtn = document.createElement('button');
    exportBtn.type = 'button';
    exportBtn.className = 'btn btn-sm preset-browser-action preset-browser-export';
    exportBtn.textContent = '↓';
    exportBtn.title = `Export "${preset.name}" as JSON`;
    exportBtn.setAttribute('aria-label', `Export ${preset.name}`);
    exportBtn.addEventListener('click', () => exportPresetAsJson(preset));

    row.append(loadBtn, exportBtn);
    return row;
  }

  private handleFactoryLoad(preset: Preset): void {
    // Factory presets aren't persisted — go straight to applyState. No
    // async awaiting, no manager.subscribe re-refresh needed.
    try {
      this.manager.applyState(preset);
      this.setActive(this.toInfo(preset, true));
      this.render();
    } catch (err) {
      console.warn('[preset-browser] factory load failed', err);
    }
  }

  private renderRow(preset: Preset): HTMLElement {
    const row = document.createElement('div');
    row.className = 'preset-browser-row';
    if (preset.id === this.activePresetId) row.classList.add('active');

    const loadBtn = document.createElement('button');
    loadBtn.type = 'button';
    loadBtn.className = 'preset-browser-load';
    loadBtn.title = `Load ${preset.name}`;

    const nameEl = document.createElement('span');
    nameEl.className = 'preset-browser-name';
    nameEl.textContent = preset.name;

    const timeEl = document.createElement('span');
    timeEl.className = 'preset-browser-time mono';
    timeEl.textContent = formatTimestamp(preset.updatedAt);

    loadBtn.append(nameEl, timeEl);
    loadBtn.addEventListener('click', () => void this.handleLoad(preset));

    const overwriteBtn = document.createElement('button');
    overwriteBtn.type = 'button';
    overwriteBtn.className = 'btn btn-sm preset-browser-action preset-browser-update';
    overwriteBtn.textContent = 'Update';
    overwriteBtn.title = `Update "${preset.name}" — rename, recategorize, save current state`;
    overwriteBtn.setAttribute('aria-label', `Update ${preset.name}`);
    overwriteBtn.addEventListener('click', () => void this.handleOverwrite(preset));

    const exportBtn = document.createElement('button');
    exportBtn.type = 'button';
    exportBtn.className = 'btn btn-sm preset-browser-action preset-browser-export';
    exportBtn.textContent = '↓';
    exportBtn.title = `Export "${preset.name}" as JSON`;
    exportBtn.setAttribute('aria-label', `Export ${preset.name}`);
    exportBtn.addEventListener('click', () => exportPresetAsJson(preset));

    const deleteBtn = document.createElement('button');
    deleteBtn.type = 'button';
    deleteBtn.className = 'btn btn-sm preset-browser-action preset-browser-delete';
    deleteBtn.textContent = '✕';
    deleteBtn.title = `Delete ${preset.name}`;
    deleteBtn.setAttribute('aria-label', `Delete ${preset.name}`);
    deleteBtn.addEventListener('click', () => void this.handleDelete(preset));

    row.append(loadBtn, overwriteBtn, exportBtn, deleteBtn);
    return row;
  }

  /**
   * Read a JSON file the user picked, run it through the migration
   * chain, and save it under their account with a fresh id (so the
   * import doesn't collide with any existing preset). Errors at any
   * step show a console warning + alert; we deliberately don't try
   * to recover from corrupt JSON because the user picked the file.
   */
  private async handleImport(file: File): Promise<void> {
    let raw: string;
    try {
      raw = await file.text();
    } catch (err) {
      console.warn('[preset-browser] import read failed', err);
      alert(`Could not read "${file.name}".`);
      return;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch (err) {
      console.warn('[preset-browser] import parse failed', err);
      alert(`"${file.name}" isn't valid JSON.`);
      return;
    }

    // Run the same migration pipeline a stored preset goes through
    // on load — handles old schema versions and stamps the result
    // as the current one. Returns null if it's unsalvageable
    // (missing required fields, future schema, etc.).
    const migrated = migratePreset(parsed);
    if (!migrated) {
      alert(`"${file.name}" doesn't look like an Evil Synth preset.`);
      return;
    }

    // Mint a fresh id + timestamps so re-importing the same file
    // produces a new entry instead of overwriting an existing one.
    // Keep the original name + category so the user can recognize
    // what they just imported.
    const now = Date.now();
    const renamed = nextAvailableName(
      migrated.name || 'Imported',
      this.presets.map((p) => p.name),
    );
    const fresh = {
      ...migrated,
      schemaVersion: CURRENT_SCHEMA_VERSION,
      id: mintPresetId(),
      name: renamed,
      createdAt: now,
      updatedAt: now,
    };

    try {
      await this.manager.applyState(fresh);
    } catch (err) {
      console.warn('[preset-browser] import applyState failed', err);
    }
    try {
      await this.manager.save(fresh.name, { category: fresh.category });
    } catch (err) {
      console.warn('[preset-browser] import save failed', err);
      alert('Imported preset could not be saved to your library.');
      return;
    }
  }
}

/**
 * Confirmation dialog for the Update button. Shows the preset name and
 * category prefilled so the user can rename or re-categorize in the same
 * action — or just confirm and overwrite in place. Returns the edited
 * {name, category} on confirm, null on cancel.
 *
 * Built from native DOM (no framework) so it fits into the existing
 * Browser DOM with no extra deps. Esc and clicking the backdrop cancel;
 * Enter in either input submits.
 */
function promptUpdatePreset(
  preset: Preset,
  categories: readonly string[],
): Promise<{ name: string; category: string } | null> {
  return new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'preset-dialog-backdrop';

    const dialog = document.createElement('div');
    dialog.className = 'preset-dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-label', 'Update preset');

    const title = document.createElement('h3');
    title.className = 'preset-dialog-title';
    title.textContent = 'Update preset';
    dialog.appendChild(title);

    const desc = document.createElement('p');
    desc.className = 'preset-dialog-desc label';
    desc.textContent =
      'Overwrite this preset with the current synth state. You can rename or move it first.';
    dialog.appendChild(desc);

    const nameLabel = document.createElement('label');
    nameLabel.className = 'preset-dialog-field';
    nameLabel.innerHTML = '<span class="label">Name</span>';
    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.className = 'preset-browser-input mono';
    nameInput.value = preset.name;
    nameLabel.appendChild(nameInput);
    dialog.appendChild(nameLabel);

    // Category: real <select> so all options are visible at a glance (unlike
    // a datalist, which native browsers often filter by the current prefix).
    // Users who need a category that doesn't exist yet pick "— new category —"
    // and type one into the revealed input.
    const catLabel = document.createElement('label');
    catLabel.className = 'preset-dialog-field';
    catLabel.innerHTML = '<span class="label">Category</span>';
    const catSelect = document.createElement('select');
    catSelect.className = 'preset-browser-input mono';

    const currentCategory = preset.category || DEFAULT_PRESET_CATEGORY;
    const allCategories = [...categories];
    // Include the preset's current category in the list if it's unusual
    // (e.g. a user typed it before and then deleted that preset).
    if (
      currentCategory !== DEFAULT_PRESET_CATEGORY &&
      !allCategories.includes(currentCategory)
    ) {
      allCategories.push(currentCategory);
      allCategories.sort();
    }
    for (const c of allCategories) {
      const o = document.createElement('option');
      o.value = c;
      o.textContent = c;
      if (c === currentCategory) o.selected = true;
      catSelect.appendChild(o);
    }
    // Always-available "uncategorized" escape hatch.
    const uncat = document.createElement('option');
    uncat.value = DEFAULT_PRESET_CATEGORY;
    uncat.textContent = DEFAULT_PRESET_CATEGORY;
    if (currentCategory === DEFAULT_PRESET_CATEGORY) uncat.selected = true;
    catSelect.appendChild(uncat);
    // Sentinel for "I want a new one". Picks reveal the custom-input below.
    const newOpt = document.createElement('option');
    newOpt.value = '__new__';
    newOpt.textContent = '— new category —';
    catSelect.appendChild(newOpt);

    catLabel.appendChild(catSelect);

    // Hidden by default, shown when user picks "— new category —".
    const customInput = document.createElement('input');
    customInput.type = 'text';
    customInput.className = 'preset-browser-input mono preset-dialog-new-category';
    customInput.placeholder = 'new category name';
    customInput.hidden = true;
    catLabel.appendChild(customInput);

    catSelect.addEventListener('change', () => {
      const isNew = catSelect.value === '__new__';
      customInput.hidden = !isNew;
      if (isNew) customInput.focus();
    });

    dialog.appendChild(catLabel);

    const actions = document.createElement('div');
    actions.className = 'preset-dialog-actions';

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'btn btn-sm';
    cancelBtn.textContent = 'Cancel';

    const confirmBtn = document.createElement('button');
    confirmBtn.type = 'button';
    confirmBtn.className = 'btn btn-sm armed';
    confirmBtn.textContent = 'Update';

    actions.append(cancelBtn, confirmBtn);
    dialog.appendChild(actions);

    const cleanup = (): void => {
      document.removeEventListener('keydown', onKey);
      backdrop.remove();
    };
    const cancel = (): void => {
      cleanup();
      resolve(null);
    };
    const confirm = (): void => {
      const name = nameInput.value.trim() || preset.name;
      // Prefer the custom input when user picked "new category"; otherwise
      // take the select's value. If they left both blank, fall back to the
      // original preset category so confirm-through doesn't accidentally
      // demote to Uncategorized.
      const category =
        catSelect.value === '__new__'
          ? customInput.value.trim() ||
            preset.category ||
            DEFAULT_PRESET_CATEGORY
          : catSelect.value || preset.category || DEFAULT_PRESET_CATEGORY;
      cleanup();
      resolve({ name, category });
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') cancel();
      else if (e.key === 'Enter' && document.activeElement !== cancelBtn) confirm();
    };

    cancelBtn.addEventListener('click', cancel);
    confirmBtn.addEventListener('click', confirm);
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) cancel();
    });
    document.addEventListener('keydown', onKey);

    backdrop.appendChild(dialog);
    document.body.appendChild(backdrop);
    // Give the name field focus + selection so Enter-Enter is "keep-as-is".
    nameInput.focus();
    nameInput.select();
  });
}

/**
 * Serialize a preset to JSON and trigger a download. Filename is the
 * slugified preset name plus `.evilsynth.json` so files are easy to
 * recognize at a glance and don't collide with other tools' .json
 * exports. The JSON is the full Preset shape — schemaVersion,
 * modules, effects, mod connections, macros, voice settings — so
 * importing the file on another browser/account reproduces the
 * patch identically.
 */
function exportPresetAsJson(preset: Preset): void {
  const json = JSON.stringify(preset, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${slugify(preset.name)}.evilsynth.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Defer revoke so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** ASCII-safe filename slug. Strips punctuation, collapses spaces
 *  to hyphens, lowercases. Falls back to "preset" when the input
 *  empties out (e.g., the name was all emoji). */
function slugify(name: string): string {
  const cleaned = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return cleaned || 'preset';
}

/**
 * Pick a name that doesn't collide with `existing`. If `base` is
 * unique, returns it as-is; otherwise appends "(2)", "(3)", … until
 * an unused suffix is found. Used by import so re-importing the same
 * file produces "Pad", "Pad (2)", "Pad (3)" instead of silently
 * overwriting whatever the user already has.
 */
function nextAvailableName(base: string, existing: readonly string[]): string {
  const taken = new Set(existing);
  if (!taken.has(base)) return base;
  for (let i = 2; i < 1000; i++) {
    const candidate = `${base} (${i})`;
    if (!taken.has(candidate)) return candidate;
  }
  // Pathological: 1000 collisions. Fall back to a timestamp.
  return `${base} (${Date.now()})`;
}

/** Shorthand relative timestamp — "5m ago", "2h ago", "Apr 22". */
function formatTimestamp(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
  if (diff < 7 * 86_400_000) return `${Math.round(diff / 86_400_000)}d ago`;
  const d = new Date(ms);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
